import { cloneColumn, cloneFacts, createPanel, newFact } from "./lib/helpers";
import { DIETARY_FACTS, DIETARY_FACTS_OLD, VITAMINERAL_FACTS, VITAMINERAL_FACTS_OLD } from "./lib/constants";
import { cloneDeep, differenceBy, isUndefined } from "lodash";
import { isNewTemplate } from "./lib/data";

const initialState = {
    selectedPanel: 0,
    isDirty: false,
    panels: [],
    globalDqErrors: [],
};

const getColumnIndex = (currentIndex, targetIndex) => {
    if (currentIndex >= targetIndex) {
        return currentIndex + 1;
    }

    return currentIndex;
};

const getNextOrderNo = (values) => {
    return Math.max(...values.map(({ order }) => order)) + 1;
};

const setSelectedPanel = (hasOnlyOneColumn, toPanel, fromPanel) => {
    if (!hasOnlyOneColumn) {
        return toPanel;
    }

    return hasOnlyOneColumn && toPanel < fromPanel ? toPanel : toPanel - 1;
};

const updateAndReorder = (action, state) => {
    const reorderPanelNo = !isUndefined(action.targetPanel) ? action.targetPanel : state.selectedPanel;
    const hasDeclaredOrder = action.value.some(({ order }) => !!order);
    const template = action.isNewTemplateUsed ? DIETARY_FACTS : DIETARY_FACTS_OLD;
    const maxOrderVal = hasDeclaredOrder
        ? Math.max(...action.value.map(({ order }) => order || 0))
        : Math.max(...template.map(({ order }) => order || 0));

    const orderedFacts = action.value
        .map((fact) => {
            const declaredNutrient = template.find(({ nutrient }) => nutrient.code === fact.nutrient.code);

            return {
                ...fact,
                order: fact.nutrient.text && declaredNutrient ? declaredNutrient.order : maxOrderVal + 1,
            };
        })
        .sort((a, b) => a.order - b.order);

    state.panels.forEach((panel, i) => {
        if (i === reorderPanelNo) {
            panel.columns[action.column][action.key].facts = action.skipReorder ? action.value : orderedFacts;
        }
    });

    return state;
};

const reducer = (state, action) => {
    switch (action.type) {
        case "RESET_DEFAULT_NUTRITION_VALUES":
            return {
                ...state,
                panels: state.panels.map((panel, i) => {
                    if (i === state.selectedPanel) {
                        return {
                            ...panel,
                            isDirty: true,
                            columns: panel.columns.map((column) => {
                                return {
                                    ...column,
                                    dietary: {
                                        facts: column.dietary.facts.map((fact) => {
                                            return {
                                                ...fact,
                                                quantity: "",
                                                pdv: {
                                                    ...fact.pdv,
                                                    value: "",
                                                },
                                            };
                                        }),
                                    },
                                };
                            }),
                        };
                    }

                    return panel;
                }),
            };
        case "RESET_DEFAULT_VITAMINS_VALUES":
            return {
                ...state,
                panels: state.panels.map((panel, i) => {
                    if (i === state.selectedPanel) {
                        const vitaminsTemplate = isNewTemplate(panel.template) ? VITAMINERAL_FACTS : VITAMINERAL_FACTS_OLD;

                        return {
                            ...panel,
                            columns: panel.columns.map((column) => {
                                return {
                                    ...column,
                                    vitamineral: {
                                        facts: cloneFacts({ facts: vitaminsTemplate }).facts,
                                    },
                                };
                            }),
                        };
                    }

                    return panel;
                }),
            };
        case "ADD_FACT":
            const withNewFact = state.panels.map((p, i) => {
                if (i === state.selectedPanel) {
                    p.columns.forEach((column) => {
                        column[action.section].facts.push(newFact());
                    });
                }

                return p;
            });

            return {
                ...state,
                isDirty: true,
                panels: withNewFact,
            };
        case "ADD_PANEL":
            const addedPanels = [...state.panels];
            const newPanel = createPanel();

            newPanel.header = action.template.header;
            newPanel.template = action.template.template;
            newPanel.columns[0].dietary.facts = action.template.dietary;
            newPanel.columns[0].vitamineral.facts = action.template.vitamineral;
            newPanel.columns[0].calorie = action.template.calorie;
            newPanel.footnotes.items = action.template.footnotes;

            addedPanels.push(newPanel);

            return {
                ...state,
                isDirty: true,
                panels: addedPanels,
                selectedPanel: addedPanels.length - 1,
            };
        case "CLONE_COLUMN":
            const withCloned = state.panels.map((p, i) => {
                if (i === state.selectedPanel) {
                    p.columns.push(cloneColumn(p.columns[p.columns.length - 1]));
                }

                return p;
            });

            return {
                ...state,
                isDirty: true,
                panels: withCloned,
            };
        case "REMOVE_COLUMN": {
            const withRemovedColumn = state.panels.map((p, i) => {
                if (i === state.selectedPanel) {
                    const newColumns = [...p.columns];
                    newColumns.splice(action.columnIndex, 1);
                    p.columns = newColumns;
                }

                return p;
            });

            return {
                ...state,
                isDirty: true,
                panes: withRemovedColumn,
            };
        }
        case "REMOVE_PANEL":
            const removedPanels = [...state.panels];
            removedPanels.splice(state.selectedPanel, 1);

            return {
                ...state,
                isDirty: true,
                panels: removedPanels,
                selectedPanel: removedPanels.length - 1,
            };
        case "UPDATE_DEEP_STATE":
            const updatedDeepPanels = state.panels.map((p, i) => {
                if (i === state.selectedPanel) {
                    p[action.section][action.key] = action.value;
                }

                return p;
            });

            return {
                ...state,
                isDirty: true,
                panels: updatedDeepPanels,
            };
        case "UPDATE_FACTS":
            updateAndReorder(action, state);

            return {
                ...state,
                isDirty: true,
                // TODO: refactor, it's a mutation - should not happen
                panels: state.panels,
            };
        case "UPDATE_STATE":
            const updatedPanels = state.panels.map((p, i) => {
                if (i === state.selectedPanel) {
                    if (action.column === false) {
                        if (p[action.key] && p[action.key].items) {
                            p[action.key].items = action.value;
                        } else {
                            p[action.key] = action.value;
                        }
                    } else {
                        p.columns[action.column][action.key] = action.value;
                    }
                }

                return p;
            });

            return {
                ...state,
                isDirty: true,
                panels: updatedPanels,
            };
        case "SAVE_PANELS":
            return {
                ...state,
                isDirty: action.isDirty,
                panels: action.panels,
            };
        case "SET_DIRTY_FLAG":
            return {
                ...state,
                isDirty: action.isDirty,
            };
        case "SET_PANEL_INDEX":
            return {
                ...state,
                selectedPanel: action.selectedPanel,
            };

        case "CLEAR_VALIDATION_WARNINGS":
            return {
                ...state,
                globalDqErrors: [],
                panels: state.panels.map((panel) => {
                    return {
                        ...panel,
                        columns: panel.columns.map((column, columnIndex) => {
                            return {
                                ...column,
                                calorie: {
                                    ...column.calorie,
                                    dataQualityIssue: undefined,
                                },
                                dietary: {
                                    ...column.dietary,
                                    facts: column.dietary.facts.map((factItem) => {
                                        return {
                                            ...factItem,
                                            nutrient: {
                                                ...factItem.nutrient,
                                                dataQualityIssue: undefined,
                                            },
                                        };
                                    }),
                                },
                            };
                        }),
                    };
                }),
            };

        case "SET_VALIDATION_WARNINGS":
            return {
                ...state,
                globalDqErrors: action.globalDqErrors,
                panels: state.panels.map((panel, panelIndex) => {
                    const updatedPanel = action.panels[panelIndex];

                    if (updatedPanel) {
                        return {
                            ...panel,
                            columns: panel.columns.map((column, columnIndex) => {
                                const updatedColumn = updatedPanel.columns[columnIndex].dietary.facts;

                                return {
                                    ...column,
                                    calorie: {
                                        ...column.calorie,
                                        dataQualityIssue: updatedPanel.columns[columnIndex].calorie.dataQualityIssue,
                                    },
                                    dietary: {
                                        ...column.dietary,
                                        facts: column.dietary.facts.map((factItem) => {
                                            const updatedItem = updatedColumn.find(
                                                ({ nutrient }) => nutrient.code === factItem.nutrient.code && !!nutrient.dataQualityIssue
                                            );

                                            if (updatedItem) {
                                                return {
                                                    ...factItem,
                                                    nutrient: {
                                                        ...factItem.nutrient,
                                                        dataQualityIssue: updatedItem.nutrient.dataQualityIssue,
                                                    },
                                                };
                                            }

                                            return {
                                                ...factItem,
                                                nutrient: {
                                                    ...factItem.nutrient,
                                                    dataQualityIssue: undefined,
                                                },
                                            };
                                        }),
                                    },
                                };
                            }),
                        };
                    }

                    return panel;
                }),
            };
        case "SHIFT_COLUMN": {
            const { fromPanel, fromColumn, toPanel, toColumn, position } = action;
            const cloneFromPanel = state.panels[fromPanel];
            const cloneToColumnCompare = state.panels[toPanel].columns[toColumn]; // take 1st index to compare nutrients;
            const cloneFromColumn = cloneFromPanel.columns[fromColumn];
            const hasOnlyOneColumn = cloneFromPanel.columns.length === 1;
            const missingNutrientsInTargetPanel = differenceBy(cloneFromColumn.dietary.facts, cloneToColumnCompare.dietary.facts, "nutrient.code");
            const missingVitamineralsInTargetPanel = differenceBy(
                cloneFromColumn.vitamineral.facts,
                cloneToColumnCompare.vitamineral.facts,
                "nutrient.text"
            );
            const insertTo = position === "before" ? (toColumn > 0 ? toColumn : 0) : toColumn + 1;
            const toPanelColumns = state.panels[toPanel].columns;
            const templateColumn = cloneDeep(state.panels[toPanel].columns[toColumn]);

            // remove cloned column
            state.panels[fromPanel].columns.splice(fromColumn, 1);
            // create placeholders for new values based on cloned target column
            state.panels[toPanel].columns.splice(insertTo, 0, {
                ...cloneFromColumn,
                dietary: {
                    ...cloneFromColumn.dietary,
                    facts: templateColumn.dietary.facts.map((fact) => {
                        return {
                            ...fact,
                            quantity: "",
                            pdv: {
                                ...fact.pdv,
                                value: "",
                            },
                        };
                    }),
                },
                vitamineral: {
                    ...cloneFromColumn.vitamineral,
                    facts: templateColumn.vitamineral.facts.map((fact) => {
                        return {
                            ...fact,
                            quantity: "",
                            pdv: {
                                ...fact.pdv,
                                value: "",
                            },
                        };
                    }),
                },
            });

            const listOfNutrientsForTargetColumn = cloneToColumnCompare.dietary.facts;
            const listOfNutrientsForClonedColumn = cloneFromColumn.dietary.facts;
            const listOfVitamineralsForTargetColumn = cloneToColumnCompare.vitamineral.facts;
            const listOfVitamineralsForClonedColumn = cloneFromColumn.vitamineral.facts;

            // iterate trough every nutrient in the target panel and update existing rows
            for (let row = 0; row < listOfNutrientsForTargetColumn.length; row++) {
                const nutrient = listOfNutrientsForTargetColumn[row].nutrient;

                // check if code exists in cloned column
                const existingCodeIndex = listOfNutrientsForClonedColumn.findIndex((nutrientRow) => nutrientRow.nutrient.code === nutrient.code);

                if (existingCodeIndex > -1) {
                    const orderNo = state.panels[toPanel].columns[insertTo].dietary.facts[row].order;
                    state.panels[toPanel].columns[insertTo].dietary.facts.splice(row, 1, {
                        ...listOfNutrientsForClonedColumn[existingCodeIndex],
                        order: orderNo,
                    });
                }
            }

            for (let row = 0; row < listOfVitamineralsForTargetColumn.length; row++) {
                const vitamineral = listOfVitamineralsForTargetColumn[row].nutrient;

                // check if code exists in cloned column
                const existingCodeIndex = listOfVitamineralsForClonedColumn.findIndex(
                    (vitamineralRow) => vitamineralRow.nutrient.text === vitamineral.text
                );

                if (existingCodeIndex > -1) {
                    state.panels[toPanel].columns[insertTo].vitamineral.facts.splice(row, 1, {
                        ...listOfVitamineralsForClonedColumn[existingCodeIndex],
                    });
                }
            }

            const nextOrderNo = getNextOrderNo(state.panels[toPanel].columns[insertTo].dietary.facts);
            state.panels[toPanel].columns[insertTo].dietary.facts = state.panels[toPanel].columns[insertTo].dietary.facts.concat(
                ...missingNutrientsInTargetPanel.map((fact, index) => {
                    return {
                        ...fact,
                        order: nextOrderNo + index,
                    };
                })
            );
            state.panels[toPanel].columns[insertTo].vitamineral.facts =
                state.panels[toPanel].columns[insertTo].vitamineral.facts.concat(missingVitamineralsInTargetPanel);

            // add missing nutrition facts from cloned column to target panel columns
            for (let col = 0; col < toPanelColumns.length; col++) {
                // create index for updated column
                const index = getColumnIndex(col, insertTo);

                if (index > toPanelColumns.length - 1) {
                    break;
                }

                for (let row = 0; row < missingNutrientsInTargetPanel.length; row++) {
                    const newRow = missingNutrientsInTargetPanel[row];
                    const orderNo = getNextOrderNo(state.panels[toPanel].columns[index].dietary.facts);

                    state.panels[toPanel].columns[index].dietary.facts.push({
                        ...newRow,
                        order: orderNo,
                        quantity: "",
                        pdv: {
                            ...newRow.pdv,
                            value: "",
                        },
                    });
                }
            }

            for (let col = 0; col < toPanelColumns.length; col++) {
                // create index for updated column
                const index = getColumnIndex(col, insertTo);

                if (index > toPanelColumns.length - 1) {
                    break;
                }

                for (let row = 0; row < missingVitamineralsInTargetPanel.length; row++) {
                    const newRow = missingVitamineralsInTargetPanel[row];

                    state.panels[toPanel].columns[index].vitamineral.facts.push({
                        ...newRow,
                        quantity: "",
                        pdv: {
                            ...newRow.pdv,
                            value: "",
                        },
                    });
                }
            }

            state.panels[toPanel].columns.forEach((col, index) => {
                updateAndReorder({ key: "dietary", column: index, value: col.dietary.facts, targetPanel: toPanel }, state);
            });

            // remove copied column from panel if it has only 1 column
            if (hasOnlyOneColumn) {
                state.panels.splice(fromPanel, 1);
            }

            state.selectedPanel = setSelectedPanel(hasOnlyOneColumn, toPanel, fromPanel);

            return state;
        }

        default:
            return state;
    }
};

export { reducer, initialState };
